import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { sluggify } from 'utils/localization/utils';
import { ACCEPTED_FILE_TYPES } from 'config/accepted-file-types';
import { hiddenFromCarrierMap } from 'config/documents';
import { sortBy, isEqual } from 'lodash';
import CollapsibleItem from 'components/v2/list/CollapsibleItem';
import CircleButton from 'components/v2/buttons/CircleButton';
import CheckIcon from 'components/v2/icons/CheckIcon';
import Dropdown from 'components/v2/dropdowns/DropdownV2';
import LoadingCircle from 'components/ui/LoadingCircle';
import CargomaticDropzone from 'components/v2/DropZone';
import UploadedDocumentPreviewModal from 'components/modules/shipments/UploadedDocumentPreviewModal';
import { DocumentMagnifyingGlassIcon } from '@heroicons/react/20/solid';
import { getShipperStopDocumentRequirementsFromConfig } from 'helpers/stops';
import {
  getSignedUrls,
  waitDocumentUpload,
  getUniqueLatestDocuments,
} from 'utils/DocumentsAPIUtils';
import { showToast } from 'stores/ToastStore';
import SessionStore from 'stores/SessionStore';
import { useHandleUploadDocuments } from 'utils/DocumentsHooks';
import { getAllShipperDocumentRequirements } from 'utils/ShipperRulesAPIUtils';

const EXCLUDED_DOCUMENTS = ['edi_logging', 'custom'];

function Document({ document, onPreview, uploader }) {
  const { t } = useTranslation();
  const { completed } = document;
  function renderStatus() {
    if (completed) {
      return <CheckIcon className="text-green-cm w-5 h-5" />;
    }
    return null;
  }

  function renderActions() {
    if (completed) {
      return (
        <div data-testid="div-2d5db973" className="flex items-center space-x-2">
          <CircleButton
            dataTestId="CircleButton-f5e2f4d5"
            size="small"
            onClick={onPreview}
          >
            <DocumentMagnifyingGlassIcon className="w-5 h-5" type="solid" />
          </CircleButton>
          <Dropdown dataTestId="Dropdown-d41808dd" useIcon>
            <Dropdown.Item
              data-testid="undefined-9ca7ce9d"
              url={document?.url}
              target="_blank"
            >
              {t('DOWNLOAD')}
            </Dropdown.Item>
            <Dropdown.Item data-testid="undefined-bbb7bd91">
              {uploader()}
            </Dropdown.Item>
          </Dropdown>
        </div>
      );
    }

    return uploader(true);
  }
  return (
    <div
      data-testid="div-d5623f65"
      className="flex justify-between text-sm text-gray-700 py-1 items-center"
    >
      <div data-testid="div-ba6a71ba" className="flex items-center space-x-2">
        <span data-testid="span-2828a3eb">{t(sluggify(document.name))}</span>
        {renderStatus()}
      </div>
      {renderActions()}
    </div>
  );
}

function usePreviousProps(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

export default function MinimumDocumentRequirements({
  shipment,
  title = 'MINIMUM_DOCUMENT_REQUIREMENTS',
}) {
  const { t } = useTranslation();
  const dropzoneRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [stopDocumentsConfig, setStopDocumentsConfig] = useState({});
  const [documents, setDocuments] = useState([]);
  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [documentToPreview, setDocumentToPreview] = useState(null);
  const [documentsUploading, setDocumentsUploading] = useState({});

  const keys = [
    shipment._id.toString(),
    ...shipment.stops.map((stop) => stop._id.toString()),
  ];

  const previous = usePreviousProps(shipment);
  useEffect(() => {
    if (shipment?.shipper && !isEqual(shipment, previous)) {
      getUploadedDocuments();
      getShipperStopDocumentRequirements();
    }
  }, [shipment]);

  useEffect(() => {
    if (shipment && Object.keys(stopDocumentsConfig).length) {
      getAllDocumentRequirements();
    }
  }, [uploadedDocuments, stopDocumentsConfig]);

  const getShipperStopDocumentRequirements = async () => {
    try {
      setLoading(true);
      const shipperId = shipment?.shipper?._id || shipment?.shipper;
      const config =
        await getShipperStopDocumentRequirementsFromConfig(shipperId);
      setStopDocumentsConfig(config);
    } catch (e) {
      showToast(
        t('ERROR_RETRIEVING_STOP_DOCUMENT_REQUIREMENTS'),
        null,
        'error',
      );
      console.error(
        `Error retrieving shipper stop documents config: ${e.message}`,
      );
    }
  };

  async function getUploadedDocuments() {
    try {
      const shipperId = shipment?.shipper?._id ?? shipment?.shipper;
      const config =
        await getShipperStopDocumentRequirementsFromConfig(shipperId);
      const documentRequirements = await getAllShipperDocumentRequirements(
        shipment,
        config,
      );
      const getDocumentsResult = await getUniqueLatestDocuments(
        keys,
        documentRequirements
      );
      const getSignedUrlsResult = await getSignedUrls(keys);
      const uploadedDocuments =
        getDocumentsResult?.data?.getUniqueLatestDocuments?.documents || [];
      const signedUrls =
        getSignedUrlsResult?.data?.getSignedUrls?.signedUrls || [];

      const documents = uploadedDocuments.map((doc) => {
        const url = signedUrls.find((url) => url.id === doc.id);
        return {
          ...doc,
          url: url?.signedUrl,
        };
      });

      setUploadedDocuments(documents);
    } catch (e) {
      console.error(`Error retrieving uploaded documents: ${e.message}`);
    }
  }

  function mapDocumentUploadStatus(documentRequirements) {
    const sortedDocuments = sortBy(
      uploadedDocuments,
      (doc) => doc.createdAt,
    ).reverse();
    const documentsStatus = documentRequirements.map((type) => {
      const uploadedDocument = sortedDocuments.find((doc) => doc.type === type);
      return {
        name: type,
        url: uploadedDocument?.url,
        completed: !!uploadedDocument,
      };
    });
    return documentsStatus;
  }

  async function getAllDocumentRequirements() {
    try {
      let allDocuments = await getAllShipperDocumentRequirements(
        shipment,
        stopDocumentsConfig,
      );

      if (SessionStore.isCarrier())
        allDocuments = allDocuments.filter((doc) => !hiddenFromCarrierMap[doc]);

      setDocuments(mapDocumentUploadStatus(allDocuments));
      setLoading(false);
    } catch (e) {
      showToast(t('ERROR_RETRIEVING_DOCUMENT_REQUIREMENTS'), null, 'error');
      console.error(`Error retrieving document requirements: ${e.message}`);
    }
  }

  async function onUpload(_shipment, _stop, _type, _uploaded, _userVisibility) {
    await getUploadedDocuments();
  }

  const { handleUpload } = useHandleUploadDocuments(shipment, null, onUpload);

  const uploadDocument = async (files, docType) => {
    try {
      dropzoneRef?.current?.open();
      let documentResult;
      documentResult = await handleUpload(
        files.map(
          (file) =>
            new File([file], docType, {
              ...file.options,
              type: file.type,
            }),
        ),
        docType,
      );
      if (documentResult?.data) {
        documentResult = await waitDocumentUpload(documentResult.data.key);
      }
      return documentResult;
    } catch (e) {
      console.log('error in uploadDocument: ', e);
    }
  };

  async function upload(files, documentType) {
    try {
      if (files.length) {
        const documentResult = await uploadDocument(files, documentType);
        return documentResult;
      }
    } catch (e) {
      showToast(t('UPLOAD_FAILED'), null, 'error');
      console.log('error in document upload: ', e);
    }
  }

  const renderRowUploader = (
    document,
    isUploading,
    setDocumentUploading,
    showIcon = false,
  ) => {
    try {
      if (isUploading) {
        return (
          <LoadingCircle
            dataTestId="LoadingCircle-646dd0ef"
            text={t('UPLOADING')}
          />
        );
      }
      return (
        <CargomaticDropzone
          dataTestId="CargomaticDropzone-f889e38f"
          dropzoneRef={dropzoneRef}
          onDrop={async (files) => {
            setDocumentUploading();
            await upload(files, document.name);
            setDocumentUploading();
          }}
          style={{ padding: 0, borderWidth: 0, borderRadius: 0 }}
          multiple={false}
          acceptedFiles={ACCEPTED_FILE_TYPES}
          disabled={isUploading}
          dropzoneMessage={!showIcon ? t('REPLACE') : ''}
          button={showIcon}
          showIcon={showIcon}
        />
      );
    } catch (e) {
      console.error(`Error rendering uploader: ${e.message}`);
    }
  };

  function documentIsUploading(name) {
    setDocumentsUploading((currentDocumentsUploading) => {
      if (currentDocumentsUploading[name]) {
        return {
          ...currentDocumentsUploading,
          [name]: false,
        };
      }
      return {
        ...currentDocumentsUploading,
        [name]: true,
      };
    });
  }

  function renderTitle() {
    return (
      <div data-testid="div-ae083836" className="mb-2">
        <h3 data-testid="h3-1a9b6644" className="text-left">
          {t(title)}
        </h3>
      </div>
    );
  }

  function documentUploader(showIcon, documentToUpload) {
    return renderRowUploader(
      documentToUpload,
      documentsUploading[documentToUpload.name],
      () => documentIsUploading(documentToUpload.name),
      showIcon,
    );
  }

  function renderContent() {
    if (loading) {
      return (
        <div data-testid="div-a014bb1d">
          <LoadingCircle
            dataTestId="LoadingCircle-9cfc31f6"
            text={t('FETCHING_REQUIREMENTS')}
          />
        </div>
      );
    }

    return (
      <div data-testid="div-a188f0ee">
        {!documents.length && (
          <p data-testid="p-8e85c987">{t('NO_DOCUMENTS_REQUIRED')}</p>
        )}
        {documents.map((document) => (
          <Document
            document={document}
            key={document.name}
            onPreview={() => setDocumentToPreview(document)}
            uploader={(showIcon) => documentUploader(showIcon, document)}
          />
        ))}
        {documentToPreview && (
          <UploadedDocumentPreviewModal
            name={documentToPreview.name}
            url={documentToPreview.url}
            onHide={() => setDocumentToPreview(null)}
          />
        )}
      </div>
    );
  }

  return (
    <CollapsibleItem
      dataTestId="CollapsibleItem-9db832fc"
      renderTitle={renderTitle}
      className="lg:col-span-4 mb-4"
      defaultOpen
    >
      {renderContent()}
    </CollapsibleItem>
  );
}
